'use client';

import { cn } from '@/libs';

import { DOTS_WINDOW_SIZE, getDotVariant, getDotsWindowStart } from './utils';

const DOT_SIZE_PX = 8;
const DOT_GAP_PX = 4;
const DOT_STEP_PX = DOT_SIZE_PX + DOT_GAP_PX;

const DOT_VARIANT_STYLES = {
  active: 'bg-primary',
  default: 'bg-foreground/30',
  edge: 'bg-foreground/30 scale-50',
} as const;

type CarouselDotsProps = {
  total: number;
  selectedIndex: number;
  className?: string;
  'data-cy'?: string;
};

export const CarouselDots = ({
  total,
  selectedIndex,
  className,
  ...props
}: CarouselDotsProps) => {
  if (total <= 1) return null;

  const windowStart = getDotsWindowStart({ total, selectedIndex });

  return (
    <div
      aria-hidden="true"
      data-cy={`${props['data-cy'] || 'results-carousel'}-dots`}
      className={cn('flex justify-center', className)}
    >
      <div
        className="overflow-hidden"
        style={{ width: Math.min(total, DOTS_WINDOW_SIZE) * DOT_STEP_PX - DOT_GAP_PX }}
      >
        <div
          className="flex w-max items-center transition-transform duration-300 ease-out"
          style={{
            gap: DOT_GAP_PX,
            transform: `translateX(-${windowStart * DOT_STEP_PX}px)`,
          }}
        >
          {Array.from({ length: total }, (_, index) => (
            <span
              key={index}
              style={{ width: DOT_SIZE_PX, height: DOT_SIZE_PX }}
              className={cn(
                'shrink-0 rounded-full transition-[transform,background-color] duration-300 ease-out',
                DOT_VARIANT_STYLES[
                  getDotVariant({ index, selectedIndex, windowStart, total })
                ],
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
